<template>
  <div class="shopping-page" :class="{ embedded }">
    <LoaderView v-if="shoppingList == null" />

    <div v-else>
      <div class="shopping-header">
        <div>
          <div class="shopping-kicker">MES COURSES</div>
          <h1>Ma liste pour la semaine</h1>
          <p>Quantités calculées à partir de mes menus, de mes portions personnalisées et des éventuels compléments MMM Sport.</p>
        </div>
        <button class="download-button" @click="downloadShoppingList">
          <font-awesome-icon class="icon" :icon="['fas', 'download']" />
          PDF
        </button>
      </div>

      <details class="menus-source">
        <summary>Voir les repas pris en compte</summary>
        <div class="menu-item-container">
          <div v-for="(dateContent, date) in shoppingList.menu_items" :key="date" class="menu-day">
            <strong>{{ humanizeDate(date) }}</strong>
            <div v-for="(menuItems, menuType) in dateContent" :key="menuType">
              <BigToggleButton v-for="(menuItem, index) in menuItems" :key="index"
                :value="menuItem.include_in_shopping_list" @input="toggleMenuItem(menuItem)">
                <div class="menu-item-meal">{{ humanizeMenuType(menuType) }}</div>
                {{ menuItem.name }}
              </BigToggleButton>
            </div>
          </div>
        </div>
      </details>

      <div class="shopping-list-title">
        <div>
          <h2>À acheter</h2>
          <span>Je coche les ingrédients achetés, récoltés ou déjà disponibles à la maison.</span>
        </div>
        <div class="shopping-manage-actions">
          <button v-if="!removalMode" type="button" class="remove-multiple-button" @click="startRemovalMode">Je retire plusieurs aliments</button>
          <template v-else>
            <button type="button" class="secondary" @click="selectAllForRemoval">Tout sélectionner</button>
            <button type="button" class="danger" :disabled="!removalIds.length || bulkRemoving" @click="bulkRemove">
              {{ bulkRemoving ? "Retrait…" : "Retirer " + removalIds.length }}
            </button>
            <button type="button" class="secondary" @click="cancelRemovalMode">Annuler</button>
          </template>
        </div>
      </div>

      <div class="ingredient-container">
        <details class="shopping-category" v-for="(ingredients, category) in ingredientsByCategory" :key="category" open>
          <summary>
            <span>{{ category || "Autre" }}</span>
            <b>{{ remainingInCategory(ingredients) }} restant{{ remainingInCategory(ingredients) > 1 ? "s" : "" }}</b>
          </summary>

          <div class="shopping-rows">
            <div
              v-for="ingredient in ingredients"
              :key="ingredient.id"
              class="shopping-row"
              :class="{ done: isChecked(ingredient) }"
            >
              <label class="shopping-check">
                <input
                  type="checkbox"
                  :checked="removalMode ? isMarkedForRemoval(ingredient) : isChecked(ingredient)"
                  @change="removalMode ? toggleRemovalMark(ingredient) : toggleChecked(ingredient)"
                />
                <span class="fake-check" :class="{ remove: removalMode }"></span>
              </label>

              <div class="shopping-product" @click="removalMode ? toggleRemovalMark(ingredient) : toggleChecked(ingredient)">
                <div class="shopping-product-name">
                  {{ ingredient.name }}
                  <span v-if="ingredient.sport_extra" class="sport-extra-badge">MMM Sport</span>
                </div>
                <div class="shopping-product-qty">{{ ingredient.quantity }} {{ ingredient.unit }}</div>
              </div>

              <button v-if="!removalMode" class="remove-product" type="button" title="Retirer de cette liste" @click="removeAlimentFast(ingredient)">×</button>
              <span v-else class="remove-mode-marker">−</span>
            </div>
          </div>
        </details>
      </div>

      <div class="mobile-shopping-bar">
        <strong>{{ checkedCount }}/{{ activeIngredientCount }}</strong>
        <span>{{ progressPercent }} % terminé</span>
      </div>
    </div>
  </div>
</template>

<script>

import { api } from "@/api";
import BigToggleButton from "@/components/BigToggleButton.vue";
import jsPDF from "jspdf";
import LoaderView from "@/components/LoaderView.vue";

export default {
  name: "MyShoppingList",
  props: { embedded: Boolean, weekNumber: { type: Number, default: null } },
  components: {
    BigToggleButton,
    LoaderView,
  },
  data() {
    return {
      shoppingList: null,
      checkedIngredientIds: [],
      removalMode: false,
      removalIds: [],
      bulkRemoving: false,
    };
  },
  computed: {
    ingredientsByCategory() {
      if (this.shoppingList == null) return {};

      const ingredientsByCategory = {};
      const categoryLabels = new Map();
      for (const ingredient of this.shoppingList.ingredients) {
        if (!ingredient.include_in_shopping_list) continue;
        const label = String(ingredient.category || "Autre").normalize("NFKC").replace(/\s+/g, " ").trim() || "Autre";
        const key = label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/œ/g, "oe").replace(/æ/g, "ae");
        if (!categoryLabels.has(key)) categoryLabels.set(key, label);
        const category = categoryLabels.get(key);
        if (ingredientsByCategory[category] == null) ingredientsByCategory[category] = [];
        ingredientsByCategory[category].push(ingredient);
      }
      return ingredientsByCategory;
    },
    activeIngredientCount() {
      return this.shoppingList?.ingredients?.filter(i => i.include_in_shopping_list).length || 0;
    },
    checkedCount() {
      return this.shoppingList?.ingredients?.filter(
        i => i.include_in_shopping_list && this.checkedIngredientIds.includes(String(i.id))
      ).length || 0;
    },
    progressPercent() {
      if (!this.activeIngredientCount) return 0;
      return Math.round(this.checkedCount * 100 / this.activeIngredientCount);
    },
  },
  mounted() {
    this.loadShoppingList();
  },
  methods: {
    shoppingParams() { return this.weekNumber == null ? {} : { week_number: this.weekNumber }; },
    loadShoppingList() {
      api.get("my-shopping-list/", { params: this.shoppingParams() }).then((response) => {
        this.shoppingList = response.data;
        this.loadChecked();
      });
    },

    storageKey() {
      const dates = Object.keys(this.shoppingList?.menu_items || {}).sort();
      return "mmm-shopping-checked:" + (dates[0] || "current");
    },

    loadChecked() {
      try {
        this.checkedIngredientIds = JSON.parse(localStorage.getItem(this.storageKey()) || "[]");
      } catch (e) {
        this.checkedIngredientIds = [];
      }
    },

    persistChecked() {
      localStorage.setItem(this.storageKey(), JSON.stringify(this.checkedIngredientIds));
    },

    isChecked(ingredient) {
      return this.checkedIngredientIds.includes(String(ingredient.id));
    },

    toggleChecked(ingredient) {
      const id = String(ingredient.id);
      if (this.checkedIngredientIds.includes(id)) {
        this.checkedIngredientIds = this.checkedIngredientIds.filter(x => x !== id);
      } else {
        this.checkedIngredientIds = [...this.checkedIngredientIds, id];
      }
      this.persistChecked();
    },

    resetChecked() {
      this.checkedIngredientIds = [];
      this.persistChecked();
    },

    remainingInCategory(ingredients) {
      return ingredients.filter(ingredient => !this.isChecked(ingredient)).length;
    },

    toggleMenuItem(menuItem) {
      // Toggle the menu item in the shopping list

      menuItem.include_in_shopping_list = !menuItem.include_in_shopping_list;

      api.post(`/my-shopping-list/${menuItem.id}/toggle/`, {}, { params: this.shoppingParams() }).then((response) => {
        this.shoppingList.ingredients = response.data.ingredients;
      });
    },
    removeAlimentFast(aliment) {
      const previous = this.shoppingList.ingredients;
      this.shoppingList.ingredients = previous.filter(item => item.id !== aliment.id);

      api.post(`my-shopping-list/aliment/${aliment.id}/toggle/`, {}, { params: this.shoppingParams() }).catch(() => {
        this.shoppingList.ingredients = previous;
      });
    },

    startRemovalMode() {
      this.removalMode = true;
      this.removalIds = [];
    },

    cancelRemovalMode() {
      this.removalMode = false;
      this.removalIds = [];
    },

    isMarkedForRemoval(ingredient) {
      return this.removalIds.includes(String(ingredient.id));
    },

    toggleRemovalMark(ingredient) {
      const id = String(ingredient.id);
      if (this.removalIds.includes(id)) {
        this.removalIds = this.removalIds.filter(value => value !== id);
      } else {
        this.removalIds = [...this.removalIds, id];
      }
    },

    selectAllForRemoval() {
      this.removalIds = (this.shoppingList?.ingredients || [])
        .filter(item => item.include_in_shopping_list)
        .map(item => String(item.id));
    },

    async bulkRemove() {
      if (!this.removalIds.length || this.bulkRemoving) return;
      const ids = this.removalIds.map(Number);
      const previous = this.shoppingList.ingredients;
      this.bulkRemoving = true;

      this.shoppingList.ingredients = previous.filter(item => !this.removalIds.includes(String(item.id)));

      try {
        const response = await api.post("my-shopping-list/aliments/bulk-remove/", { ids }, { params: this.shoppingParams() });
        this.shoppingList.ingredients = response.data.ingredients;
        this.removalMode = false;
        this.removalIds = [];
      } catch (error) {
        this.shoppingList.ingredients = previous;
      } finally {
        this.bulkRemoving = false;
      }
    },
    async downloadShoppingList() {
      const doc = new jsPDF();

      const logoWidth = 50;
      const logoHeight = 20;
      const pageWidth = doc.internal.pageSize.getWidth();
      const logoX = pageWidth - logoWidth - 20;
      const logoY = 10;
      const logoUrl = '/logo.png';
      const imageData = await this.getBase64Image(logoUrl);

      // ✅ HEADER : logo + titre
      const drawHeader = () => {
        doc.addImage(imageData, 'PNG', logoX, logoY, logoWidth, logoHeight);
        doc.setFontSize(18);
        doc.text("Ma Liste de Courses", 20, 26);
        doc.setLineWidth(0.5);
        doc.line(20, 30, pageWidth - 20, 30);
      };

      drawHeader();

      // --- Layout ---
      let yOffset = 40;
      const marginLeft = 20;
      const columnGap = 10;
      const columnWidth = (pageWidth - marginLeft * 2 - columnGap) / 2;
      const lineHeight = 6;
      let xOffset = marginLeft;

      // ✅ Police par défaut
      doc.setFontSize(10);

      for (const category in this.ingredientsByCategory) {
        // Nouvelle page si trop bas
        if (yOffset > 270) {
          if (xOffset === marginLeft) {
            xOffset += columnWidth + columnGap;
            yOffset = 40;
          } else {
            doc.addPage();
            xOffset = marginLeft;
            yOffset = 40;
            // ✅ Réapplique le header et la taille de police correcte
            drawHeader();
            doc.setFontSize(10);
          }
        }

        // ✅ Catégorie
        doc.setFontSize(11);
        doc.text(`Rayon ${category}`, xOffset, yOffset);
        yOffset += lineHeight;

        // ✅ Ingrédients
        doc.setFontSize(9);
        for (const ingredient of this.ingredientsByCategory[category]) {
          if (!ingredient.include_in_shopping_list) continue;

          if (yOffset > 270) {
            if (xOffset === marginLeft) {
              xOffset += columnWidth + columnGap;
              yOffset = 40;
            } else {
              doc.addPage();
              xOffset = marginLeft;
              yOffset = 40;
              // ✅ Réapplique le header et la taille du texte ici aussi
              drawHeader();
              doc.setFontSize(10);
            }
          }

          const line = `${ingredient.name} x ${ingredient.quantity} ${ingredient.unit}`;

          // Découpe automatique du texte à la largeur de la colonne
          const wrappedText = doc.splitTextToSize(line, columnWidth - 4); // marge intérieure légère

          for (const txtLine of wrappedText) {
            if (yOffset > 270) {
              if (xOffset === marginLeft) {
                xOffset += columnWidth + columnGap;
                yOffset = 40;
              } else {
                doc.addPage();
                xOffset = marginLeft;
                yOffset = 40;
                drawHeader();
                doc.setFontSize(10);
              }
            }
            doc.text(txtLine, xOffset, yOffset);
            yOffset += lineHeight;
          }
        }

        yOffset += lineHeight;
      }

      doc.save("liste-de-course.pdf");
    },
    humanizeDate(isoDate) {
      const date = new Date(isoDate);
      const res = date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
      // Capitalize the first letter
      return res.charAt(0).toUpperCase() + res.slice(1);
    },
    humanizeMenuType(menuType) {
      switch (menuType) {
        case "breakfast":
          return "Petit-déjeuner";
        case "lunch_starter":
          return "Entrée du déjeuner";
        case "lunch_main":
          return "Plat principal du déjeuner";
        case "lunch_dessert":
          return "Dessert du déjeuner";
        case "diner_starter":
          return "Entrée du dîner";
        case "diner_main":
          return "Plat principal du dîner";
        case "diner_dessert":
          return "Dessert du dîner";
        case "snack":
          return "En-cas";
        default:
          return menuType;
      }
    },
    getBase64Image(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.setAttribute("crossOrigin", "anonymous");
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = reject;
        img.src = url;
      });
    }
  },
};



</script>


<style scoped>
.shopping-page {
  padding: 20px;
  max-width: 1180px;
  margin: 0 auto;
}
.shopping-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}
.shopping-kicker {
  color: #789b4a;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: .12em;
}
.shopping-header h1 {
  margin: 2px 0 5px;
  color: #315b19;
  font-size: 30px;
}
.shopping-header p {
  margin: 0;
  max-width: 720px;
  color: #66705f;
  line-height: 1.4;
}
.download-button {
  border: 0;
  border-radius: 12px;
  padding: 10px 14px;
  background: #6f963a;
  color: #fff;
  font-weight: 800;
  cursor: pointer;
}
.shopping-progress-card {
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 16px;
  padding: 14px 16px;
  margin-bottom: 14px;
  border: 1px solid #dfe8d4;
  border-radius: 14px;
  background: #f8fbf3;
}
.shopping-progress-card strong { color: #315b19; }
.shopping-progress-card span { display: block; color: #74806d; font-size: 12px; }
.shopping-progress-card button {
  align-self: center;
  border: 0;
  background: transparent;
  color: #52782f;
  font-weight: 800;
  cursor: pointer;
}
.shopping-progress-track {
  grid-column: 1 / -1;
  height: 7px;
  margin-top: 7px;
  border-radius: 99px;
  background: #e2ead8;
  overflow: hidden;
}
.shopping-progress-fill {
  height: 100%;
  background: #789b4a;
  transition: width .2s ease;
}
.menus-source {
  margin-bottom: 22px;
  border: 1px solid #e1e7da;
  border-radius: 12px;
  background: #fff;
}
.menus-source summary {
  padding: 12px 14px;
  color: #526348;
  font-weight: 800;
  cursor: pointer;
}
.menu-item-container {
  display: flex;
  gap: 10px;
  padding: 0 12px 12px;
  overflow-x: auto;
}
.menu-day {
  flex: 0 0 210px;
  display: grid;
  gap: 7px;
}
.menu-item-meal {
  font-size: 11px;
  font-weight: 800;
  color: #436f0e;
}
.shopping-list-title {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 15px;
  margin: 4px 0 12px;
}
.shopping-manage-actions {
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.shopping-manage-actions button {
  border: 1px solid #d5dfca;
  border-radius: 9px;
  padding: 8px 10px;
  background: #f7faf4;
  color: #436f0e;
  font-weight: 800;
  cursor: pointer;
}
.shopping-manage-actions button.danger {
  border-color: #e0c8c1;
  background: #fff6f3;
  color: #9b4737;
}
.shopping-manage-actions button:disabled {
  opacity: .45;
  cursor: default;
}
.shopping-list-title h2 {
  margin: 0;
  color: #315b19;
  font-size: 24px;
}
.shopping-list-title span {
  color: #74806d;
  font-size: 12px;
}
.ingredient-container {
  display: grid;
  gap: 10px;
}
.shopping-category {
  border: 1px solid #dfe5d7;
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
}
.shopping-category > summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 13px 15px;
  background: #f6f8f3;
  color: #315b19;
  font-weight: 900;
  cursor: pointer;
}
.shopping-category > summary b {
  color: #7b8575;
  font-size: 11px;
}
.shopping-rows {
  display: grid;
}
.shopping-row {
  display: grid;
  grid-template-columns: 34px 1fr 34px;
  align-items: center;
  min-height: 58px;
  padding: 5px 8px 5px 12px;
  border-top: 1px solid #edf0e9;
}
.shopping-row.done {
  background: #f8faf6;
}
.shopping-row.done .shopping-product-name,
.shopping-row.done .shopping-product-qty {
  color: #9ba398;
  text-decoration: line-through;
}
.shopping-check {
  position: relative;
  width: 26px;
  height: 26px;
  margin: 0;
  cursor: pointer;
}
.shopping-check input {
  position: absolute;
  opacity: 0;
}
.fake-check {
  display: block;
  width: 24px;
  height: 24px;
  border: 2px solid #9fbd68;
  border-radius: 7px;
  background: #fff;
}
.shopping-check input:checked + .fake-check {
  background: #789b4a;
  border-color: #789b4a;
}
.shopping-check input:checked + .fake-check.remove {
  background: #b85c4a;
  border-color: #b85c4a;
}
.shopping-check input:checked + .fake-check::after {
  content: "✓";
  display: block;
  color: #fff;
  text-align: center;
  line-height: 20px;
  font-weight: 900;
}
.shopping-product {
  min-width: 0;
  cursor: pointer;
}
.shopping-product-name {
  color: #263322;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.2;
}
.shopping-product-qty {
  margin-top: 2px;
  color: #66705f;
  font-size: 12px;
}
.sport-extra-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 5px;
  border-radius: 5px;
  background: #eef6e4;
  color: #52782f;
  font-size: 8px;
  vertical-align: 2px;
}
.remove-product {
  border: 0;
  background: transparent;
  color: #a4aca0;
  font-size: 22px;
  cursor: pointer;
}
.remove-mode-marker {
  color: #b85c4a;
  font-size: 20px;
  font-weight: 900;
  text-align: center;
}
.mobile-shopping-bar {
  display: none;
}

@media (max-width: 700px) {
  .shopping-page {
    padding: 12px 10px 80px;
  }
  .shopping-header {
    align-items: flex-start;
  }
  .shopping-header h1 {
    font-size: 24px;
  }
  .shopping-header p {
    font-size: 12px;
  }
  .download-button {
    padding: 8px 10px;
  }
  .shopping-list-title {
    display: block;
  }
  .shopping-manage-actions {
    margin-top: 10px;
    justify-content: flex-start;
    position: sticky;
    top: 0;
    z-index: 3;
    padding: 7px 0;
    background: #fff;
  }
  .shopping-list-title span {
    display: block;
    margin-top: 3px;
  }
  .shopping-category > summary {
    position: sticky;
    top: 0;
    z-index: 1;
  }
  .shopping-row {
    min-height: 64px;
    grid-template-columns: 38px 1fr 32px;
    padding-left: 10px;
  }
  .shopping-product-name {
    font-size: 15px;
  }
  .shopping-product-qty {
    font-size: 13px;
  }
  .mobile-shopping-bar {
    position: fixed;
    left: 8px;
    right: 8px;
    bottom: 8px;
    z-index: 1100;
    display: flex;
    justify-content: space-between;
    padding: 10px 14px;
    border-radius: 12px;
    background: #315b19;
    color: #fff;
    box-shadow: 0 8px 24px rgba(30, 50, 20, .2);
  }
  .mobile-shopping-bar span {
    opacity: .85;
  }
}
.shopping-manage-actions .remove-multiple-button { background: #436f0e; color: #fff; border: 2px solid #436f0e; padding: 12px 18px; font-size: 15px; font-weight: 700; border-radius: 10px; box-shadow: 0 3px 8px rgba(40,65,15,.14); }
.shopping-manage-actions .remove-multiple-button:hover { background: #34580b; }
.shopping-manage-actions .remove-multiple-button:focus-visible { outline: 3px solid #92b969; outline-offset: 3px; }
.shopping-page.embedded .mobile-shopping-bar { position: static; margin-top: 16px; }
.shopping-page.embedded { padding-bottom: 20px; }
</style>
